/**
 * useSubscription composable
 * Provides reactive subscription state and helpers for plan management.
 */
import { ref, computed } from "vue";
import axios from "axios";
import { openExternal } from "@/utils/native";

const PLAN_RANK = { free: 0, premium: 1, business: 2 };

export function planRank(plan) {
  return PLAN_RANK[plan] ?? 0;
}

const currentPlan = ref("free");
const subscription = ref(null);
const loading = ref(false);

export async function fetchSubscriptionStatus() {
  const token = localStorage.getItem("auth-token");
  if (!token) {
    currentPlan.value = "free";
    subscription.value = null;
    return;
  }
  loading.value = true;
  try {
    const res = await axios.get("/api/subscription/status", {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });
    subscription.value = res.data;
    currentPlan.value = res.data.plan || "free";
  } catch {
    currentPlan.value = "free";
    subscription.value = null;
  } finally {
    loading.value = false;
  }
}

export async function createCheckout(plan) {
  // Route through our in-app embedded checkout page so users see our header
  // + back button. The page itself calls createEmbeddedCheckoutSession.
  window.location.href = `/checkout?plan=${encodeURIComponent(plan)}`;
}

export async function createEmbeddedCheckoutSession(plan) {
  const token = localStorage.getItem("auth-token");
  if (!token) throw new Error("Not authenticated");
  const res = await axios.post(
    "/api/subscription/create-embedded-checkout",
    { plan },
    {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    }
  );
  return res.data; // { clientSecret, attemptID, checkoutSessionID }
}

export async function fetchCheckoutSessionStatus(sessionId) {
  const token = localStorage.getItem("auth-token");
  if (!token) throw new Error("Not authenticated");
  const res = await axios.get("/api/subscription/session-status", {
    params: { session_id: sessionId },
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });
  return res.data;
}

export async function syncSubscription() {
  const token = localStorage.getItem("auth-token");
  if (!token) return;
  try {
    const res = await axios.post(
      "/api/subscription/sync",
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      }
    );
    if (res.data.plan) {
      currentPlan.value = res.data.plan;
    }
    // Refresh full status after sync
    await fetchSubscriptionStatus();
  } catch {
    // Fallback — just fetch status
    await fetchSubscriptionStatus();
  }
}

export async function openCustomerPortal() {
  const token = localStorage.getItem("auth-token");
  if (!token) throw new Error("Not authenticated");
  const res = await axios.post(
    "/api/subscription/create-portal",
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    }
  );
  if (res.data.url) {
    // System browser on native — navigating the WebView to Stripe's hosted
    // portal would strand the user outside the bundled app.
    await openExternal(res.data.url);
  }
}

export async function cancelSubscription() {
  const token = localStorage.getItem("auth-token");
  if (!token) throw new Error("Not authenticated");
  const res = await axios.post(
    "/api/subscription/cancel",
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    }
  );
  await fetchSubscriptionStatus();
  return res.data;
}

export async function reactivateSubscription() {
  const token = localStorage.getItem("auth-token");
  if (!token) throw new Error("Not authenticated");
  const res = await axios.post(
    "/api/subscription/reactivate",
    {},
    {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    }
  );
  await fetchSubscriptionStatus();
  return res.data;
}

export function useSubscription() {
  return {
    currentPlan,
    subscription,
    loading,
    fetchSubscriptionStatus,
    syncSubscription,
    createCheckout,
    openCustomerPortal,
    cancelSubscription,
    reactivateSubscription,
    planRank,
    isPremiumOrAbove: computed(() =>
      ["premium", "business"].includes(currentPlan.value)
    ),
    isBusiness: computed(() => currentPlan.value === "business"),
    isFree: computed(() => currentPlan.value === "free"),
    cancelAtPeriodEnd: computed(() => subscription.value?.cancelAtPeriodEnd || false),
    currentPeriodEnd: computed(() => subscription.value?.currentPeriodEnd || null),
  };
}
